import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReviewPrNowModal from './ReviewPrNowModal';

const SEV_COLORS ={ error: 'var(--danger)', warning: 'var(--warning)', info: 'var(--accent)' };
function fmtDate(v) { if (!v) return '—'; try { return new Date(v).toLocaleString(); } catch { return v; } }

function SevBadge({ s }) {
  const c = SEV_COLORS[s] || 'var(--text-muted)';
  return <span style={{ fontSize: 11, fontWeight: 700, padding: '1px 7px', borderRadius: 999, background: `${c}22`, color: c, textTransform: 'uppercase' }}>{s}</span>;
}

function Findings({ prResultId }) {
  const [findings, setFindings] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    axios.get(`/code-review/pr-results/${prResultId}/findings`)
      .then(res => setFindings(res.data.data || []))
      .catch(e => setError(e.response?.data?.error || 'Failed to load findings'));
  }, [prResultId]);

  if (error) return <div className="error-msg">{error}</div>;
  if (findings === null) return <div style={{ padding: 12 }}><div className="spinner" style={{ width: 16, height: 16 }} /></div>;
  if (findings.length === 0) return <div style={{ padding: 12, color: 'var(--text-muted)', fontSize: 13 }}>No findings.</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '8px 0' }}>
      {findings.map(fd => (
        <div key={fd.id} style={{ background: 'var(--bg-elevated)', borderRadius: 'var(--radius)', padding: '10px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
            <SevBadge s={fd.severity} />
            <code style={{ fontSize: 12 }}>{fd.file_path}:{fd.line}</code>
            {fd.category && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>· {fd.category}</span>}
            {fd.major_feature && <span style={{ fontSize: 11, color: 'var(--accent)' }}>· {fd.major_feature}</span>}
            {fd.confidence != null && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>· conf {Number(fd.confidence).toFixed(2)}</span>}
            {fd.posted ? <span className="badge badge-success" style={{ marginLeft: 'auto' }}>posted</span>
              : <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text-muted)' }}>not posted (dry-run)</span>}
          </div>
          <div style={{ fontWeight: 600, fontSize: 13 }}>{fd.title}</div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', whiteSpace: 'pre-wrap', marginTop: 2 }}>{fd.body}</div>
          {fd.suggestion && (
            <pre style={{ background: 'var(--bg-surface)', borderRadius: 6, padding: 8, fontSize: 12, marginTop: 6, overflowX: 'auto' }}>{fd.suggestion}</pre>
          )}
        </div>
      ))}
    </div>
  );
}

export default function ReviewRunDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [run, setRun] = useState(null);
  const [error, setError] = useState('');
  const [open, setOpen] = useState({});
  const [stopping, setStopping] = useState(false);
  const [reviewPrUrl, setReviewPrUrl] = useState('');
  const timerRef = useRef(null);

  const load = useCallback((silent) => {
    axios.get(`/code-review/runs/${id}`)
      .then(res => setRun(res.data))
      .catch(e => { if (!silent) setError(e.response?.data?.error || 'Failed to load run'); });
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Poll live while the run is in progress so metrics + PR list update.
  const isRunning = run && run.status === 'running';
  useEffect(() => {
    if (!isRunning) return undefined;
    timerRef.current = setInterval(() => load(true), 5000);
    return () => clearInterval(timerRef.current);
  }, [isRunning, load]);

  async function stopRun() {
    if (!window.confirm('Stop this run? It halts after the current PR finishes; PRs already reviewed are kept.')) return;
    setStopping(true);
    try {
      await axios.post(`/code-review/runs/${id}/stop`);
      load(true);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to stop run');
    } finally {
      setStopping(false);
    }
  }

  if (error) return <div className="page-body"><div className="error-msg">{error}</div></div>;
  if (!run) return <div className="page-body"><div className="spinner" /></div>;

  // Derive live metrics from the per-PR rows (run-level totals are only written
  // at finish, so during a run these give accurate live numbers).
  const prs = run.pr_results || [];
  const completed = run.prs_completed ?? prs.length;
  const reviewedOk = prs.filter(p => p.status === 'reviewed' || p.status === 'posted').length;
  const errors = prs.filter(p => p.status === 'error').length;
  const liveFindings = prs.reduce((n, p) => n + (p.findings_count || 0), 0);
  const liveCost = prs.reduce((n, p) => n + Number(p.cost_usd || 0), 0);
  const liveTokens = prs.reduce((n, p) => n + (p.input_tokens || 0) + (p.output_tokens || 0), 0);
  const selected = run.prs_selected || 0;
  const pct = selected ? Math.min(100, Math.round((completed / selected) * 100)) : 0;

  return (
    <div>
      <div className="page-header">
        <h2>Run #{run.id}</h2>
        <p>{fmtDate(run.started_at)} · {run.post_mode} · {run.cancel_requested && run.status === 'running' ? 'stopping…' : run.status}</p>
      </div>
      <div className="page-body">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <button className="btn btn-ghost" onClick={() => navigate('/code-review/runs')}>← All runs</button>
          <div className="spacer" style={{ flex: 1 }} />
          {run.status === 'running' && (
            <button className="btn btn-secondary" style={{ color: 'var(--danger)' }}
              disabled={stopping || run.cancel_requested} onClick={stopRun}>
              {run.cancel_requested ? 'Stopping…' : 'Stop run'}
            </button>
          )}
        </div>

        {selected > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>
              <span>{completed} of {selected} PRs {run.status === 'running' ? 'reviewed so far' : 'processed'}</span>
              <span>{pct}%</span>
            </div>
            <div style={{ height: 6, background: 'var(--bg-elevated)', borderRadius: 999 }}>
              <div style={{ width: `${pct}%`, height: '100%', borderRadius: 999, background: run.status === 'running' ? 'var(--warning)' : 'var(--accent)' }} />
            </div>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(120px,1fr))', gap: 12, marginBottom: 20 }}>
          {[
            { label: 'Selected', value: selected },
            { label: 'Completed', value: completed },
            { label: 'Reviewed OK', value: reviewedOk },
            { label: 'Errors', value: errors },
            { label: 'Skipped', value: run.prs_skipped },
            { label: 'Findings', value: liveFindings },
            { label: 'Posted', value: run.posted_count },
            { label: 'Tokens', value: liveTokens.toLocaleString() },
            { label: 'Cost', value: `$${liveCost.toFixed(4)}` },
          ].map(s => (
            <div key={s.label} style={{ background: 'var(--bg-elevated)', borderRadius: 'var(--radius)', padding: '10px 14px' }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{s.label}</div>
              <div style={{ fontWeight: 700, fontSize: 18, color: s.label === 'Errors' && errors > 0 ? 'var(--danger)' : undefined }}>{s.value}</div>
            </div>
          ))}
        </div>
        {run.summary && <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 16 }}>{run.summary}</p>}

        <h3 style={{ fontSize: 15 }}>Reviewed PRs</h3>
        {(run.pr_results || []).length === 0 ? <div className="empty-state"><p>No PRs in this run.</p></div> : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {run.pr_results.map(pr => (
              <div key={pr.id} style={{ border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', background: 'var(--bg-surface)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', cursor: 'pointer' }}
                  onClick={() => setOpen(o => ({ ...o, [pr.id]: !o[pr.id] }))}>
                  <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>{open[pr.id] ? '▾' : '▸'}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>#{pr.pr_id} {pr.pr_title || '(no title)'}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                      {pr.repo_full_name} → {pr.target_branch} · {pr.pr_author || '—'} · {(pr.files_reviewed != null && pr.files_reviewed < pr.changed_files)
                        ? `${pr.files_reviewed}/${pr.changed_files} files reviewed`
                        : `${pr.changed_files} files`}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', fontSize: 12 }}>
                    <div><b>{pr.findings_count}</b> findings{pr.status === 'error' ? ' · ⚠ error' : ''}</div>
                    <div style={{ color: 'var(--text-muted)' }}>{(pr.input_tokens + pr.output_tokens).toLocaleString()} tok · ${Number(pr.cost_usd).toFixed(4)}</div>
                  </div>
                  <button className="btn btn-secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}
                    title="Run a fresh review of this PR now, picking up any new commits"
                    onClick={e => { e.stopPropagation(); setReviewPrUrl(pr.pr_url); }}>
                    Review again
                  </button>
                  <a href={pr.pr_url} target="_blank" rel="noreferrer" className="btn btn-secondary" style={{ fontSize: 12 }} onClick={e => e.stopPropagation()}>Open PR ↗</a>
                </div>
                {pr.skip_reason && <div style={{ padding: '0 16px 10px', fontSize: 12, color: 'var(--danger)' }}>{pr.skip_reason}</div>}
                {open[pr.id] && <div style={{ padding: '0 16px 12px' }}><Findings prResultId={pr.id} /></div>}
              </div>
            ))}
          </div>
        )}
      </div>
      {reviewPrUrl && (
        <ReviewPrNowModal defaultUrl={reviewPrUrl} defaultConfigId={run.review_config_id || ''}
          onClose={() => setReviewPrUrl('')} />
      )}
    </div>
  );
}
